import React, { useState, useMemo, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import { useMsal } from "@azure/msal-react";
import { Box, CircularProgress, Typography } from "@mui/material";
import TrashCanIcon from "../../../assets/icons/TrashCanIcon";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import PrimaryTextField from "../../Inputs/PrimaryTextField/PrimaryTextField";
import SecondaryButton from "../../Buttons/SecondaryButton/SecondaryButton";
import CustomizedTable from "../../CustomizedTable/CustomizedTable";
import { DefaultCell } from "../../CustomizedTable/CustomCells/CustomCells";
import SelectedCell from "../../CustomizedTable/CustomCells/SelectedCell/SelectedCell";
import ActionsCell from "../../CustomizedTable/CustomCells/ActionsCell/ActionsCell";
import CarrierRepNameCell from "../../CustomizedTable/CustomCells/CarrierRepNameCell/CarrierRepNameCell";
import DateTimeRangeCell from "../../CustomizedTable/CustomCells/DateTimeRangeCell/DateTimeRangeCell";
import CapacityDayCell from "../../CustomizedTable/CustomCells/CapacityDayCell/CapacityDayCell";
import ProposedCostCell from "../../CustomizedTable/CustomCells/ProposedCostCell/ProposedCostCell";
import NumberRangeFilter from "../../CustomizedTable/Filters/NumberRangeFilter/NumberRangeFilter";
import SelectedFilter from "../../CustomizedTable/Filters/SelectedFilter/SelectedFilter";
import DateRangeFilter from "../../CustomizedTable/Filters/DateRangeFilter/DateRangeFilter";
import MultiOptionsFilter from "../../CustomizedTable/Filters/MultipleOptionsFilter/MultipleOptionsFilter";
import SelectColumnFilter from "../../CustomizedTable/Filters/SelectColumnFilter/SelectColumnFilter";
import useEquipmentTypes from "../../../hooks/useEquipmentTypes/useEquipmentTypes";
import { getSumOfArray } from "../../../utils/calcHelpers";
import { removeDuplicatesFromArray } from "../../../utils/dataHelpers";
import { sortArrayOfDateObjectsDesc } from "../../../utils/sortHelpers";
import { CarrierService } from "../../../services/CarrierService";
import { AlertContext } from "../../Dashboard/Dashboard";

export default function CarrierProfilePreferredLanes({
  activeUser,
  activeCarrier,
  handleTableAction
}) {
  const { instance } = useMsal();
  const { typeModes } = useEquipmentTypes();
  const [search, setSearch] = useState("");
  const [capacityLanes, setCapacityLanes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedLanes, setSelectedLanes] = useState([]);
  const { warning } = useContext(AlertContext);

  const lanes = useMemo(() => capacityLanes, [capacityLanes]);

  const columns = useMemo(
    () => [
      {
        Header: "",
        accessor: "selected",
        Filter: SelectedFilter,
        width: 50,
        Cell: SelectedCell,
        sortDisabled: true,
        showFilter: true
      },
      {
        Header: "Actions",
        id: "actions",
        userIdMcld: activeUser.userIdMcld,
        activeCarrier: activeCarrier,
        originPoint: "preferred-lane",
        actions: ["edit-capacity", "expire-capacity"],
        width: 70,
        Cell: ActionsCell,
      },
      {
        Header: "Start Date",
        accessor: "startDate",
        Filter: DateRangeFilter,
        filter: "dateBetween",
        dateOnly: true,
        width: 130,
        Cell: DateTimeRangeCell,
      },
      {
        Header: "End Date",
        accessor: "endDate",
        Filter: DateRangeFilter,
        filter: "dateBetween",
        dateOnly: true,
        width: 120,
        Cell: DateTimeRangeCell,
      },
      {
        Header: "Mode",
        accessor: "mode",
        Filter: SelectColumnFilter,
        filter: "equal",
        width: 100,
        Cell: DefaultCell,
      },
      {
        Header: "Origin City",
        accessor: "originCity",
        filter: "fuzzyText",
        width: 180,
        Cell: DefaultCell,
      },
      {
        Header: "OS",
        accessor: "originState",
        Filter: MultiOptionsFilter,
        filter: "multi",
        width: 90,
        Cell: DefaultCell,
      },
      {
        Header: "Dest City",
        accessor: "destinationCity",
        filter: "fuzzyText",
        width: 180,
        Cell: DefaultCell,
      },
      {
        Header: "DS",
        accessor: "destinationState",
        Filter: MultiOptionsFilter,
        filter: "multi",
        width: 90,
        Cell: DefaultCell,
      },
      {
        Header: "Daily",
        id: "capacity-daily",
        filter: "fuzzyText",
        width: 260,
        Cell: CapacityDayCell,
      },
      {
        Header: "Weekly",
        accessor: "weekly",
        filter: "fuzzyText",
        width: 110,
        Cell: DefaultCell,
      },
      {
        Header: "Rate",
        accessor: "proposedCost",
        Filter: NumberRangeFilter,
        filter: "between",
        width: 120,
        Cell: ProposedCostCell,
      },
      {
        Header: "Date Posted",
        accessor: "postedDate",
        Filter: DateRangeFilter,
        filter: "dateBetween",
        dateOnly: true,
        width: 140,
        Cell: DateTimeRangeCell,
      },
      {
        Header: "Posted By",
        accessor: "userDisplay",
        filter: "fuzzyText",
        width: 140,
        Cell: CarrierRepNameCell,
      },
    ],
    []
  );

  const handleSelectAll = value => {
    let lanes = [...capacityLanes];
    lanes.forEach(offer => { offer.selected = value; });
    setCapacityLanes(lanes);
  };

  const parseRow = row => {
    const weeklySum = getSumOfArray(Object.values(row.availableDaily).filter(daily => daily));
    row.weekly = row.weekly + weeklySum;

    const modes = [];
    Object.values(row.equipmentTypeDaily).forEach(type => modes.push(typeModes[type]));
    row.mode = removeDuplicatesFromArray(modes.filter(type => type)).join(",");

    if (row.proposedRpm && !row.proposedCost && row.miles) {
      row.proposedCost = row.proposedRpm * row.miles;
    } else if (row.proposedRpm && !row.proposedCost && !row.miles) {
      row.proposedCost = row.proposedRpm;
    }

    if (row.updatedBy) {
      row.userDisplay = row.updatedBy;
      row.dispatcherUserName = row.userUpdatedBy.name;
    } else {
      row.userDisplay = row.postedBy;
      row.dispatcherUserName = row.userCreatedBy.name;
    }

    row.selected = false;

    return row;
  };

  const parseResults = rows => {
    let parsedRows = rows.map(row => parseRow(row));
    const sortedRows = sortArrayOfDateObjectsDesc(parsedRows, "postedDate");

    return sortedRows;
  };

  const updateData = (rowIndex, columnID, value) => {
    setCapacityLanes(old =>
      old.map((row, index) => {
        if (index === rowIndex) {
          return {
            ...row,
            [columnID]: value,
          };
        }
        return row;
      })
    );
  };

  useEffect(() => {
    if (activeCarrier.id) {
      setIsLoading(true);
      CarrierService.GetPreferredLanes(instance, activeCarrier.id).then(res => {
        setCapacityLanes(parseResults(res));
        setIsLoading(false);
      }).catch(error => {
        console.error(error);
        setIsLoading(false);
        warning(error);
      });
    }

  }, [activeCarrier]);

  useEffect(() => {
    let lanes = capacityLanes.filter(({ selected }) => selected === true);
    setSelectedLanes(lanes);
  }, [capacityLanes]);

  return (
    <Box className="Carrier-profile-component">
      <Box className="Carrier-profile-component-header">
        <Typography variant="h3" color="black_opacity.100">
          Preferred Lanes
        </Typography>
        <Box sx={{ display: "flex" }}>
          <Box sx={{ marginRight: "10px" }}>
            <SecondaryButton
              label="Expire"
              onClick={() => handleTableAction({ action: "expire-capacity", userIdMcld: activeUser.userIdMcld, activeCarrier: activeCarrier, originPoint: "preferred-lane", selectedLanes: selectedLanes })}
              startIcon={TrashCanIcon}
              disabled={selectedLanes.length == 0}
              size="small"
            />
          </Box>
          <Box sx={{ marginRight: "10px" }}>
            <SecondaryButton
              label="Add"
              onClick={() => handleTableAction({ action: "carrier-capacity", subaction: "add-capacity", type: "preferred-lane", carrierId: activeCarrier.id })}
              startIcon={AddOutlinedIcon}
              size="small"
            />
          </Box>
          <PrimaryTextField
            placeholder={`Search ${lanes.length} results`}
            onChange={setSearch}
            value={search}
            isSearch
          />
        </Box>
      </Box>
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
          <CircularProgress sx={{ color: "primary.60" }} size={40} />
        </Box>
      ) : (
        <CustomizedTable
          id="preferred-lanes"
          columns={columns}
          data={lanes}
          tableSearch={search}
          updateData={updateData}
          setSelectAll={handleSelectAll}
          actionCallback={handleTableAction}
        />
      )}
    </Box>
  );
}

CarrierProfilePreferredLanes.propTypes = {
  activeUser: PropTypes.object.isRequired,
  activeCarrier: PropTypes.object.isRequired,
  refreshLanes: PropTypes.bool,
  setRefreshLanes: PropTypes.func,
  handleTableAction: PropTypes.func
};